import React, { useState, useEffect } from "react";
import { Table, Card, Input, Select, message } from "antd";
import LeaveService from "../../Services/Manage/LeaveService";

const { Option } = Select;

const leaveTypes = [
  { leaveType: "Annual Leave", numberOfLeaves: 20 },
  { leaveType: "Sick Leave", numberOfLeaves: 15 },
];

function calculateLeaveDays(fromDate, toDate, status) {
  if (status !== "Approve") return 0;
  const from = new Date(fromDate);
  const to = new Date(toDate);
  from.setHours(0, 0, 0, 0);
  to.setHours(0, 0, 0, 0);
  const diffTime = to.getTime() - from.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
}

function ManageLeave() {
  const [leaveData, setLeaveData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedDepartments, setSelectedDepartments] = useState([]);



useEffect(() => {
  const fetchLeaveData = async () => {
    setLoading(true);
    try {
      const res = await LeaveService.getAllLeave();
      const data = Array.isArray(res?.data) ? res.data : [];

      const transformed = data.map((item, index) => {
        const leaveType = leaveTypes.find((l) => l.leaveType === item.leave_reason);
        const totalLeaves = leaveType?.numberOfLeaves || 0;

        const today = new Date().toISOString().split("T")[0];
        const isOnLeaveToday =
          item.status === "Approve" &&
          today >= item.from_date &&
          today <= item.to_date;

        const from = new Date(item.from_date);
        const to = new Date(item.to_date);

        let dayDiff = 0;

        if (item.status === "Approve") {
          if (item.day_count != null && !isNaN(item.day_count)) {
            dayDiff = Number(item.day_count);
          } else {
            from.setHours(0, 0, 0, 0);
            to.setHours(0, 0, 0, 0);
            const diffTime = to.getTime() - from.getTime();
            dayDiff = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
          }
        }

        const options = { year: "numeric", month: "short", day: "numeric" };
        const formattedFrom = from.toLocaleDateString(undefined, options);
        const formattedTo = to.toLocaleDateString(undefined, options);
        const leavePeriod = `${formattedFrom} – ${formattedTo}`;

        return {
          key: item.id || index,
          userId: item.raised_by_user_id,
          name: item.raised_by_user_name,
          department: item.department_name,
          leaveType: item.leave_type,
          fromDate: item.from_date,
          toDate: item.to_date,
          leavePeriod,
          onLeaveToday: isOnLeaveToday,
          claimedLeave: dayDiff,
          remainingLeave: Math.max(0, totalLeaves - dayDiff),
        };
      });

      setLeaveData(transformed);
    } catch (error) {
      message.error("Failed to load leave data.");
    } finally {
      setLoading(false);
    }
  };

  fetchLeaveData();
}, []);


  const departments = [...new Set(leaveData.map((item) => item.department).filter(Boolean))];

  const filteredData = leaveData.filter((record) => {
    if (selectedDepartments.length > 0 && !selectedDepartments.includes(record.department)) return false;
    const searchableFields = [
      record.userId,
      record.name,
      record.department,
      record.leaveType,
      record.onLeaveToday ? "yes" : "no",
      record.fromDate,
      record.toDate,
    ];
    const lowerSearch = searchText.toLowerCase();
    return searchableFields.some(
      (field) => field != null && field.toString().toLowerCase().includes(lowerSearch)
    );
  });

  const sortedData = [...filteredData].sort((a, b) => b.onLeaveToday - a.onLeaveToday);

  const columns = [
    {
      title: "S.No",
      key: "serial",
      width: 60,
      align: "center",
      render: (_, __, index) => index + 1,
    },
    {
      title: "User ID",
      dataIndex: "userId",
      key: "userId",
      align: "center",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      align: "center",
    },
    {
      title: "Department",
      dataIndex: "department",
      key: "department",
      align: "center",
    },
    {
      title: "Leave Type",
      dataIndex: "leaveType",
      key: "leaveType",
      align: "center",
    },
    {
      title: "On Leave",
      dataIndex: "onLeaveToday",
      key: "onLeaveToday",
      align: "center",
      render: (onLeaveToday) => (
        <span style={{ color: onLeaveToday ? "green" : "gray" }}>{onLeaveToday ? "Yes" : "No"}</span>
      ),
    },
    {
      title: "Leave Period",
      dataIndex: "leavePeriod",
      key: "leavePeriod",
      align: "center",
    },
    {
      title: "Total Leave Days",
      dataIndex: "claimedLeave",
      key: "claimedLeave",
      align: "center",
    },
  ];

  return (
    <div style={{ background: "#fff", borderRadius: 6 }}>
      <Card
        style={{ marginBottom: 24, borderRadius: 6 }}
        bodyStyle={{ display: "flex", alignItems: "flex-end" }}
      >
        <div style={{ width: 350, marginRight: 16 }}>
          <label htmlFor="searchInput" style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
            Search
          </label>
          <Input
            id="searchInput"
            placeholder="Search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            size="large"
          />
        </div>
        <div style={{ width: 350 }}>
          <label
            htmlFor="departmentSelect"
            style={{ display: "block", marginBottom: 6, fontWeight: 600 }}
          >
            Select Department
          </label>
          <Select
            id="departmentSelect"
            mode="multiple"
            allowClear
            placeholder="Select Department(s)"
            value={selectedDepartments}
            onChange={setSelectedDepartments}
            size="large"
            style={{ width: "100%" }}
          >
            {departments.map((dept) => (
              <Option key={dept} value={dept}>
                {dept}
              </Option>
            ))}
          </Select>
        </div>
      </Card>
      <Table
        columns={columns}
        dataSource={sortedData}
        pagination={false}
        scroll={{ x: "max-content" }}
        rowKey="key"
        loading={loading}
      />
    </div>
  );
}

export default ManageLeave;
